/* eslint-disable @typescript-eslint/no-unused-vars */
import { IconDownload, IconX } from "@tabler/icons-react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import { VALIDATION_MAPPING } from "../constants/validationMapping";

type Row = Record<string, any>;

type BulkResponseModalProps = {
  onClose: () => void;
  title?: string;
  data: {
    successful?: Row[];
    failed?: Row[];
    successfulRows?: number;
    failedRows?: number;
    errors?: { rowData: Row; errorMessage: string }[];
    totalRows?: number;
  };
  config: {
    failKey?: "failed" | "errors";
    failColumns: { key: string; header: string }[]; // UI only
    allColumns: { key: string; header: string }[]; // ← NEW: for Excel
    sheetName: string;
    downloadFileName: string;
    validationMessages: string[];
    note?: string;
  };
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    onPageChange: (newPage: number) => void;
    isLoading?: boolean;
  };
  onDownloadAll?: () => Promise<any[]>;
};

export default function BulkResponseModal({
  onClose,
  title = "Upload Status Report",
  data,
  config,
  pagination,
  onDownloadAll,
}: BulkResponseModalProps) {
  const {
    failKey = "failed",
    failColumns,
    sheetName,
    downloadFileName,
    validationMessages,
    note,
  } = config;

  const hasSuccess = () =>
    data.successfulRows !== undefined && data.successfulRows > 0;
  const hasFail = () => {
    if (data.failedRows !== undefined) return data.failedRows > 0;
    const arr = data[failKey] as any[] | undefined;
    return arr?.length ? true : false;
  };

  const failRows = () => {
    if (data.failedRows !== undefined) return data.failedRows;
    const arr = data[failKey] as any[] | undefined;
    return arr?.length ?? 0;
  };

  const fg = (hex: string) => ({
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: hex.replace("#", "") },
  });
  const centerWrap = {
    vertical: "middle" as const,
    horizontal: "center" as const,
    wrapText: true,
  };
  const thinBorder = {
    top: { style: "thin" as const },
    left: { style: "thin" as const },
    bottom: { style: "thin" as const },
    right: { style: "thin" as const },
  };
  const styleInfoRow = (row: ExcelJS.Row) => {
    row.eachCell((cell) => {
      cell.font = { bold: true, size: 11, color: { argb: "212121" } };
      cell.alignment = { ...centerWrap, horizontal: "left" as const };
      cell.fill = fg("FFFFFD");
    });
  };

  const handleDownloadFailed = async () => {
    let failedItems: any[] = [];
    if (onDownloadAll && (data.failedRows ?? 0) > (data.errors?.length ?? 0)) {
      const toastId = toast.loading(
        `Fetching all ${data.failedRows?.toLocaleString()} failed records for export...`
      );
      try {
        failedItems = await onDownloadAll();
        toast.dismiss(toastId);
      } catch (err) {
        toast.dismiss(toastId);
        toast.error("Failed to load records for export");
        return;
      }
    } else {
      failedItems = data.errors ?? (data[failKey] as any[]) ?? [];
    }

    if (!failedItems?.length) {
      toast.error("No failed data available to download");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet(sheetName);

      // 1. Extract keys from first failed row
      const firstItem = failedItems[0];
      const rowObj = firstItem.rowData ?? firstItem;
      const dataKeys = Object.keys(rowObj);

      // 2. Build Excel columns: data keys + reason
      const excelColumns = [
        ...dataKeys.map((key) => ({
          key,
          header: key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase()),
        })),
        { key: "reason", header: "Reason" },
      ];

      const lastCol = String.fromCharCode(65 + excelColumns.length - 1);

      // Info
      const infoText = `This file contains failed ${sheetName} import records. Please fix the highlighted issues and re-upload.`;
      const infoRow = ws.addRow([infoText]);
      ws.mergeCells(`A1:${lastCol}1`);
      styleInfoRow(infoRow);

      // Validation row – map to actual columns
      const validationRowData = excelColumns.map((col) => {
        const key = col.key;
        return key === "reason"
          ? "Reason (System generated, not required)"
          : VALIDATION_MAPPING[key] || key;
      });

      const validationRow = ws.addRow(validationRowData);
      validationRow.eachCell((cell, i) => {
        const isReason = excelColumns[i - 1].key === "reason";
        cell.fill = fg(isReason ? "FFEB9C" : "FFCCCC");
        cell.font = { bold: true, size: 10, color: { argb: "212121" } };
        cell.alignment = centerWrap;
        cell.border = thinBorder;
      });
      validationRow.height = 50;

      // Header
      const headerRow = ws.addRow(excelColumns.map((c) => c.header));
      headerRow.eachCell((cell, i) => {
        const isReason = excelColumns[i - 1].key === "reason";
        cell.fill = fg(isReason ? "FFCCCC" : "217447");
        cell.font = { bold: true, size: 11, color: { argb: "FFFFFF" } };
        cell.alignment = centerWrap;
        cell.border = thinBorder;
      });
      headerRow.height = 20;

      // Data
      failedItems.forEach((item) => {
        const row = item.rowData ?? item;
        const values = excelColumns.map((col) => {
          if (col.key === "reason")
            return item.errorMessage ?? item.reason ?? "";
          return row[col.key] ?? "";
        });
        const dataRow = ws.addRow(values);
        dataRow.eachCell((cell, i) => {
          if (excelColumns[i - 1].key === "reason") {
            cell.fill = fg("FFCCCC");
            cell.font = { color: { argb: "990000" } };
          }
          cell.alignment = centerWrap;
          cell.border = thinBorder;
        });
      });

      ws.columns = excelColumns.map(() => ({ width: 25 }));

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${downloadFileName}.xlsx`);
      toast.success("Failed data downloaded successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate Excel file");
    }
  };

  const showSuccessCount = hasSuccess();
  const showFail = hasFail();

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="rounded-2xl overflow-hidden max-w-2xl w-11/12">
        {/* Header */}
        <div className="flex items-center">
          <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl rounded-tr-[63px]">
            <h2 className="text-xl">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
          >
            <IconX size={20} />
          </button>
        </div>

        <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] px-6 py-4 rounded-tl-none rounded-[30px] space-y-4">
          {showSuccessCount && (
            <div className="p-3 bg-green-500/10 rounded-lg border border-[#D2D2D2] text-center">
              <p className="text-sm font-medium text-green-800">
                Successfully Processed:{" "}
                <span className="font-bold">{data.successfulRows}</span> rows
              </p>
            </div>
          )}

          {showFail && (
            <FailedBlock
              items={data.errors ?? (data[failKey] as any[]) ?? []}
              columns={failColumns}
              count={failRows()}
              pagination={pagination}
            />
          )}

          {showFail && (
            <div className="flex items-center justify-between gap-4 bg-white px-4 py-2 rounded-md border border-[#D2D2D2]">
              <p className="text-xs font-medium text-[#212121]">
                <b>Note:</b>{" "}
                {note ??
                  `We found ${failRows()} record${
                    failRows() > 1 ? "s" : ""
                  } failed to upload. Review the CSV file, correct the errors, and re-upload.`}
              </p>
              <button
                onClick={handleDownloadFailed}
                className="bg-[#212121] text-white flex items-center gap-1 px-4 py-2 rounded-md text-sm flex-shrink-0"
              >
                <IconDownload size={20} />
                <span>Failed Data</span>
              </button>
            </div>
          )}

          {!showFail && showSuccessCount && (
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              <SuccessSvg />
              <p className="text-xl font-medium">Data Uploaded Successfully</p>
              <p className="text-sm text-[#212121]/70 text-center max-w-md">
                All records were processed successfully.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SuccessSvg() {
  return (
    <svg
      height="60"
      width="60"
      viewBox="0 0 520 520"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="_68-Contract">
        <path
          d="m80.144 30v406.177h179.195v-20l110.661-110.654h20v-275.523z"
          fill="#d8d8d8"
        />
        <g fill="#707070">
          <path d="m144.45 118.951h181.277v20h-181.277z" />
          <path d="m144.45 174.813h181.277v20h-181.277z" />
          <path d="m144.45 230.675h181.277v20h-181.277z" />
          <path d="m144.45 286.538h54.666v20h-54.666z" />
        </g>
        <path
          d="m339.884 488a99.956 99.956 0 1 1 99.955-99.955 100.069 100.069 0 0 1 -99.955 99.955z"
          fill="#b0ef8f"
        />
        <path
          d="m316.09 444.313-28.789-31.988 14.865-13.379 13.43 14.921 61.792-73.313 15.293 12.89z"
          fill="#009045"
        />
      </g>
    </svg>
  );
}

function FailedBlock({
  items,
  columns,
  count,
  pagination,
}: {
  items: any[];
  columns: { key: string; header: string }[];
  count: number;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    onPageChange: (newPage: number) => void;
    isLoading?: boolean;
  };
}) {
  const currentPage = pagination?.currentPage ?? 1;
  const totalPages = pagination?.totalPages ?? 1;
  const startRow = (currentPage - 1) * 100 + 1;
  const endRow = Math.min(currentPage * 100, count);

  return (
    <div className="bg-red-500/10 rounded-xl border border-[#D2D2D2] flex flex-col overflow-hidden">
      <div className="p-3 flex flex-wrap items-center justify-between gap-2 border-b border-[#D2D2D2]/60 bg-white/40">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-slate-800">
            Failed Data List
          </p>
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
            {count.toLocaleString()} {count === 1 ? "Row" : "Rows"}
          </span>
        </div>
        {pagination && pagination.totalPages > 1 && (
          <p className="text-xs text-slate-600 font-medium">
            Showing rows <span className="font-semibold text-slate-900">{startRow}</span> to{" "}
            <span className="font-semibold text-slate-900">{endRow}</span> of{" "}
            <span className="font-semibold text-slate-900">{count.toLocaleString()}</span>
          </p>
        )}
      </div>

      <div className="max-h-[22rem] overflow-y-auto scrollbar-clean relative">
        {pagination?.isLoading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10">
            <span className="text-xs font-semibold text-slate-700 animate-pulse">Loading page {currentPage}...</span>
          </div>
        )}
        <Table
          columns={columns.map((c) => ({
            ...c,
            render:
              c.key === "reason"
                ? (row: any) => row.errorMessage ?? row.reason ?? ""
                : undefined,
          }))}
          data={items}
          startIndex={startRow}
        />
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-white border-t border-[#D2D2D2]/60">
          <button
            type="button"
            disabled={pagination.currentPage <= 1 || pagination.isLoading}
            onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-sm text-slate-700 cursor-pointer"
          >
            &larr; Previous Page
          </button>

          <span className="text-xs text-slate-600">
            Page <span className="font-bold text-slate-900">{pagination.currentPage}</span> of{" "}
            <span className="font-bold text-slate-900">{pagination.totalPages.toLocaleString()}</span>
          </span>

          <button
            type="button"
            disabled={pagination.currentPage >= pagination.totalPages || pagination.isLoading}
            onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-sm text-slate-700 cursor-pointer"
          >
            Next Page &rarr;
          </button>
        </div>
      )}
    </div>
  );
}

type TableColumn = {
  key: string;
  header: string;
  render?: (row: any) => any;
};

function Table({
  columns,
  data,
  startIndex = 1,
}: {
  columns: TableColumn[];
  data: any[];
  startIndex?: number;
}) {
  return (
    <table className="w-full px-4 border border-dashed border-gray-300">
      <thead className="text-[#212121]/90 font-bold border-y border-dashed border-gray-300 bg-[#E0E0E0] sticky top-0 z-[5]">
        <tr>
          <th className="text-left p-1.5 px-2 text-xs font-bold border-r border-dashed border-gray-300">#</th>
          {columns.map((c) => (
            <th key={c.key} className="text-left p-1.5 px-2 text-xs font-bold border-r border-dashed border-gray-300 last:border-none">
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {data.map((row, idx) => (
          <tr key={idx} className="text-xs hover:bg-[#FBD037]/10 transition">
            <td className="p-2 border-r border-dashed border-gray-200 text-gray-500 font-mono">
              {startIndex + idx}
            </td>
            {columns.map((c) => (
              <td key={c.key} className="p-2 border-r border-dashed border-gray-200 last:border-none">
                {c.render ? c.render(row) : (row.rowData ?? row)[c.key] ?? ""}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const fg = (c: string) =>
  ({ type: "pattern", pattern: "solid", fgColor: { argb: c } } as any);
const centerWrap = {
  horizontal: "center",
  vertical: "middle",
  wrapText: true,
} as any;
const thinBorder = {
  top: { style: "thin", color: { argb: "000000" } },
  left: { style: "thin", color: { argb: "000000" } },
  bottom: { style: "thin", color: { argb: "000000" } },
  right: { style: "thin", color: { argb: "000000" } },
} as any;

function styleInfoRow(row: any) {
  row.eachCell((cell: any) => {
    cell.fill = fg("E2EFD9");
    cell.font = { bold: true, size: 10, color: { argb: "212121" } };
    cell.alignment = { ...centerWrap, horizontal: "left" };
    cell.border = thinBorder;
  });
  row.height = 25;
}
